import React, { useMemo, useState } from 'react';
import { Alert, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PartiesRoutes } from '../../../Router/path';
import ActionButtons from '../../Shared/Components/ActionButtons';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import DataTable from '../../Shared/Components/DataTable';
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import { extractPartiesErrorFeedback } from '../Service/partiesMapper';
import { useDeletePartyMutation, useSearchPartiesQuery } from '../Service/partiesApi';
import { PartyIsActiveFilter } from '../Types/partyTypes';
import { partyColumns } from '../columns';

const Parties = () => {
  const navigate = useNavigate();
  const [selectedPartyIds, setSelectedPartyIds] = useState<number[]>([]);
  const [searchDraft, setSearchDraft] = useState('');
  const [isActiveDraft, setIsActiveDraft] = useState<PartyIsActiveFilter>('active');
  const [queryState, setQueryState] = useState<{ search: string; isActive: PartyIsActiveFilter }>({
    search: '',
    isActive: 'active',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [deleteParty] = useDeletePartyMutation();

  const queryArgs = useMemo(
    () => ({
      search: queryState.search,
      isActive: queryState.isActive,
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: 'name',
      sortDir: 'asc' as const,
    }),
    [page, queryState.isActive, queryState.search, rowsPerPage]
  );

  const { data, isLoading, isError, error } = useSearchPartiesQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const handleDeleteSelected = useDeleteSelectedItems<number>(
    selectedPartyIds,
    (id) => deleteParty(id).unwrap(),
    () => setSelectedPartyIds([]),
    {
      errorPrefix: 'Nie udało się zdezaktywować wybranych stron.',
      onDeleteError: (deleteError) => {
        const feedback = extractPartiesErrorFeedback(
          deleteError,
          'Nie udało się zdezaktywować wybranych stron.'
        );
        setDeleteErrorMessage(feedback.message);
      },
    }
  );

  const errorMessage = extractPartiesErrorFeedback(
    error,
    'Nie udało się pobrać listy stron. Spróbuj ponownie.'
  ).message;

  return (
    <Stack spacing={3}>
      <CustomCard title="Strony (Parties)" subheader="CRUD i wyszukiwanie stron dla panelu admin">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Szukaj po nazwie, NIP, e-mailu lub mieście"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            fullWidth
          />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="party-active-filter-label">Status</InputLabel>
            <Select
              labelId="party-active-filter-label"
              label="Status"
              value={isActiveDraft}
              onChange={(event) => setIsActiveDraft(event.target.value as PartyIsActiveFilter)}
            >
              <MenuItem value="all">Wszystkie</MenuItem>
              <MenuItem value="active">Tylko aktywne</MenuItem>
              <MenuItem value="inactive">Tylko nieaktywne</MenuItem>
            </Select>
          </FormControl>
          <ActionButtons
            createLabel="Dodaj stronę"
            deleteLabel="Dezaktywuj zaznaczone"
            onCreate={() => navigate(PartiesRoutes.create)}
            onDeleteSelected={handleDeleteSelected}
            sx={{ mb: 0 }}
          />
        </Stack>
        <Stack direction="row" spacing={2} mt={2}>
          <ActionButtons
            createLabel="Szukaj"
            onCreate={() => {
              setPage(0);
              setSelectedPartyIds([]);
              setDeleteErrorMessage(null);
              setQueryState({ search: searchDraft.trim(), isActive: isActiveDraft });
            }}
            sx={{ mb: 0 }}
          />
          <ActionButtons
            createLabel="Wyczyść filtry"
            onCreate={() => {
              setPage(0);
              setSearchDraft('');
              setIsActiveDraft('active');
              setSelectedPartyIds([]);
              setQueryState({ search: '', isActive: 'active' });
              setDeleteErrorMessage(null);
            }}
            sx={{ mb: 0 }}
          />
        </Stack>
      </CustomCard>

      {deleteErrorMessage && <Alert severity="error">{deleteErrorMessage}</Alert>}

      <CustomCard>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Aktywne filtry: fraza "{queryState.search || '-'}", status "{queryState.isActive}".
        </Typography>
        <DataTable
          columns={partyColumns}
          data={data?.items || []}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          emptyMessage="Brak wyników dla podanych kryteriów."
          getRowId={(row) => row.id}
          onEdit={(id) => navigate(PartiesRoutes.update.replace(':partyId', id.toString()))}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.total || 0}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(0);
          }}
          selected={selectedPartyIds}
          onSelectionChange={setSelectedPartyIds}
          editingRowId={null}
          setEditingRowId={() => {}}
        />
      </CustomCard>
    </Stack>
  );
};

export default Parties;
